// Damage math shared by the damage, projectile and zone systems.
// Pure and Phaser-free (tests/combat-damage.test.ts).

import type { DamageInfo, StatBlock, Tag } from '../../core/types';

/** Default bonus from the `vulnerable` status (+25%). */
export const VULNERABLE_BONUS = 0.25;
/** Shocked targets take +15% electric damage. */
export const SHOCK_ELECTRIC_BONUS = 0.15;
/** Incoming damage multiplier while the held weapon is overheated. */
export const OVERHEAT_VULNERABILITY = 1.25;
export const MAX_ARMOR = 0.6;
/** Damage at the very edge of an explosion relative to its centre. */
export const EXPLOSION_EDGE_FACTOR = 0.5;

export type OutgoingStats = Pick<StatBlock, 'damage' | 'explosionDamage' | 'chainDamage' | 'burnPower'>;

export interface OutgoingFactors {
  source: DamageInfo['source'];
  tags: readonly Tag[];
  isDot: boolean;
  stats: OutgoingStats;
  /** abilities.outgoingMultiplier(target, info). */
  abilityMult: number;
  /** Bonus fraction from the target's vulnerable status (0 when not vulnerable). */
  vulnerableBonus: number;
  /** Target currently has shock stacks. */
  shocked: boolean;
}

/**
 * Outgoing multiplier (everything except crits):
 * player sources: stats.damage × abilities × tag stats (explosive → explosionDamage,
 * chain → chainDamage, burn DoT → burnPower); any source: vulnerable (+25%) × shocked-vs-electric (+15%).
 */
export function outgoingMultiplier(f: OutgoingFactors): number {
  let m = 1;
  if (f.source === 'player') {
    m *= f.stats.damage * f.abilityMult;
    if (f.tags.includes('explosive')) m *= f.stats.explosionDamage;
    if (f.tags.includes('chain')) m *= f.stats.chainDamage;
    if (f.isDot && f.tags.includes('burn')) m *= f.stats.burnPower;
  }
  if (f.vulnerableBonus > 0) m *= 1 + f.vulnerableBonus;
  if (f.shocked && f.tags.includes('electric')) m *= 1 + SHOCK_ELECTRIC_BONUS;
  return Number.isFinite(m) ? Math.max(0, m) : 0;
}

/** Crit check: `roll` is a uniform [0,1) sample from the combat rng. */
export function isCrit(roll: number, critChance: number): boolean {
  return roll < Math.max(0, Math.min(1, critChance));
}

/** Explosion damage factor by distance from the centre (to the target's edge): 1 at the centre → 0.5 at the rim. */
export function explosionFalloff(dist: number, radius: number, edgeFactor = EXPLOSION_EDGE_FACTOR): number {
  if (radius <= 0) return 1;
  const t = Math.max(0, Math.min(1, dist / radius));
  return 1 - (1 - edgeFactor) * t;
}

/** Range falloff (shotgun pellets): 1 until `start`, lerp to `min` at `end`. */
export function rangeFalloff(travelled: number, f: { start: number; end: number; min: number } | undefined): number {
  if (!f) return 1;
  if (travelled <= f.start) return 1;
  if (travelled >= f.end || f.end <= f.start) return f.min;
  const t = (travelled - f.start) / (f.end - f.start);
  return 1 + (f.min - 1) * t;
}

/** Incoming player damage before shields/armour. */
export function incomingDamage(amount: number, damageTaken: number, incomingMult: number, weaponOverheated: boolean): number {
  const v = amount * Math.max(0, damageTaken) * Math.max(0, incomingMult) * (weaponOverheated ? OVERHEAT_VULNERABILITY : 1);
  return Number.isFinite(v) ? Math.max(0, v) : 0;
}

export interface PlayerDamageSplit {
  /** Taken by the shield. */
  absorbed: number;
  /** Lost from hp after armour. */
  hpDamage: number;
  shieldLeft: number;
}

/** Shield absorbs first; the remainder is reduced by armour (clamped to 0..0.6) and goes to hp. */
export function splitPlayerDamage(amount: number, shield: number, armor: number): PlayerDamageSplit {
  const a = Math.max(0, amount);
  const s = Math.max(0, shield);
  const absorbed = Math.min(s, a);
  const rest = a - absorbed;
  const arm = Math.max(0, Math.min(MAX_ARMOR, armor));
  return { absorbed, hpDamage: rest * (1 - arm), shieldLeft: s - absorbed };
}

/**
 * Several knockback impulses on one target in one frame (shotgun pellets, overlapping explosions)
 * add up as vectors but never exceed 1.5× the strongest single impulse.
 */
export function capKnockback(sumX: number, sumY: number, maxSingle: number): { x: number; y: number } {
  const mag = Math.sqrt(sumX * sumX + sumY * sumY);
  const cap = maxSingle * 1.5;
  if (mag <= cap || mag <= 0) return { x: sumX, y: sumY };
  const k = cap / mag;
  return { x: sumX * k, y: sumY * k };
}
